import type { Collection } from 'src/apis/api-utilities';
import type {
  DateInterval,
  Identifier,
  LotRuntimePeriodType,
  NetworkPoint,
  Price,
  ProcedureType,
  ProposalComponent,
  ProposalTransactionType,
  ProposalType,
  Quantity,
  SimpleNetworkPoint,
  SimpleTradeProposal,
  TradeResponse,
  TradeResponseStatus,
  TradeResponseSummary,
  ViewProposalType,
} from 'src/apis/monolith/types';
import { labelsToOptions } from 'src/utils/labels-to-options';
import { labelsToValues } from 'src/utils/labels-to-values';

export const detailedProposalLabels: Record<ViewProposalType, string> = {
  PROPOSAL_TO_BUY_ASSIGNMENT: 'Proposal to Buy (Assignment)',
  PROPOSAL_TO_BUY_TRANSFER_OF_USE: 'Proposal to Buy (Transfer of Use)',
  PROPOSAL_TO_SELL_ASSIGNMENT: 'Proposal to Sell (Assignment)',
  PROPOSAL_TO_SELL_TRANSFER_OF_USE: 'Proposal to Sell (Transfer of Use)',
};

export const detailedProposalValues = labelsToValues(detailedProposalLabels);

export const detailedProposalOptions = labelsToOptions(detailedProposalLabels);

function assertDetailedProposalType(
  value: string
): asserts value is ViewProposalType {
  if (
    value === 'PROPOSAL_TO_BUY_ASSIGNMENT' ||
    value === 'PROPOSAL_TO_BUY_TRANSFER_OF_USE' ||
    value === 'PROPOSAL_TO_SELL_ASSIGNMENT' ||
    value === 'PROPOSAL_TO_SELL_TRANSFER_OF_USE'
  ) {
    type TypeCheck<T extends ViewProposalType = typeof value> = T;
  } else {
    throw new Error(`${value} is not of type "DetailedProposalType".`);
  }
}

function assertDetailedProposalTypes(
  values: string[]
): asserts values is ViewProposalType[] {
  values.forEach(assertDetailedProposalType);
}

export type ProposalActionType =
  | 'RESPOND'
  | 'DELETE'
  | 'SHOW_DETAILS'
  | 'REMOVE_TRADER_LIST'
  | 'SUBMIT'
  | 'EDIT';

export type TradeProposalState =
  // PENDING:
  | 'NEW'
  | 'OPEN'
  | 'CLOSED'
  // SUCCESSFULL:
  | 'FULFILLED'
  // UNSUCCESSFULL:
  | 'DELETED'
  | 'WITHDRAWN'
  | 'REJECTED'
  | 'CANCELLED'
  | 'EXPIRED';

export const procedureTypeLabels: Record<ProcedureType, string> = {
  CFO: 'Call for Order (CFO)',
  FCFS: 'First-Come-First-Serve (FCFS)',
  OTC: 'Over the Counter (OTC)',
};

export const procedureTypeOptions = labelsToOptions(procedureTypeLabels);

function assertProcedureType(value: string): asserts value is ProcedureType {
  if (value === 'CFO' || value === 'FCFS' || value === 'OTC') {
    type TypeCheck<T extends ProcedureType = typeof value> = T;
  } else {
    throw new Error(`${value} is not of type "ProcedureType".`);
  }
}

function assertProcedureTypes(
  values: string[]
): asserts values is ProcedureType[] {
  values.forEach(assertProcedureType);
}

type TradeParticipant = {
  userName: string; // optional?
  companyName: string; // optional?
  userEmail: string; // optional?
};

type ProposalSummary = {
  id: number;
  // responseReceived: boolean;
  // favoriteNetworkPoint: boolean;
  // state: TradeProposalState;
  // ownProposalWithResponses: boolean;
  // ownProposal: boolean;
  // ownResponse: boolean;
  proposalInitiator?: TradeParticipant;
  networkPointRef: number;
  networkPoint: SimpleNetworkPoint;
  transactionType: ProposalTransactionType;
  proposalType: ProposalType;
  tradingProcedure: ProcedureType;
  // amount: Quantity;
  // price: Price;
  // runtime: DateInterval;
  // expirationDate: string;
  submissionWindowClosure: string;
  // createdBy: string;
  // createdAt: string;
  // completelyAnonymous: boolean;
  // minimumLotSize: QuantityDto;
  // minimumLotRuntime: MinimumLotRuntime;
  // otcCounterparty: Shipper;
  // otcEmails: string;
  // traderList: string;
  // tradingConditionsName: string;
  // tradingConditionsId: number;
  // combineUnbundledDeals: boolean;
  // I think it's allways one for non-bundled networkpoints and two for bundled networkpoints:
  components: [ProposalComponent] | [ProposalComponent, ProposalComponent];
  // actions: ProposalDtoActionType[];
  // responseList: ResponseDto[];
  // comment: string;
};

type MinimumLotRuntime = {
  type: LotRuntimePeriodType;
  value: number;
};

export type Proposal = {
  id: number;
  // responseReceived?: boolean; // it looks like this is never set, even when there are responses
  favoriteNetworkPoint?: boolean;
  state: TradeProposalState;
  ownProposalWithResponses?: boolean;
  ownProposal?: boolean;
  ownResponse?: boolean;
  proposalInitiator?: TradeParticipant;
  networkPointRef: number;
  networkPoint: NetworkPoint; // we might need a new type which extends NetworkPoint with additional data
  transactionType: ProposalTransactionType;
  proposalType: ProposalType;
  tradingProcedure: ProcedureType;
  amount: Quantity;
  price: Price; // seems to be optional?
  runtime: DateInterval;
  expirationDate: string;
  submissionWindowClosure?: string;
  createdBy?: string;
  createdAt?: string;
  completelyAnonymous?: boolean;
  minimumLotSize?: Quantity;
  minimumLotRuntime?: MinimumLotRuntime;
  otcCounterparty?: {
    name?: string /* because in angular code it was referred as "$ctrl.proposal.otcCounterparty.name" */;
    identifierList: Identifier[];
  };
  otcEmails?: string;
  traderList?: string;
  tradingConditionsName: string; // optional?
  tradingConditionsId: number;
  // combineUnbundledDeals: boolean;
  // I think it's allways one for non-bundled networkpoints and two for bundled networkpoints:
  components: [ProposalComponent] | [ProposalComponent, ProposalComponent];
  actions: ProposalActionType[];
  responseList?: TradeResponse[];
  comment?: string;
};

export type SecondaryTradeSummary = {
  // the rest.d.ts is wrong - it says field "proposal" has the type ProposalDto,
  // but it is actually just _some_ fields of the ProposalDto
  // see de.tracx.pkp.rest.service.AbstractSecondaryResource#mapProposal
  proposal: ProposalSummary;
  response: TradeResponseSummary;
  state: TradeResponseStatus;
  createdAt?: string;
  ownProposal?: boolean;
  ownResponse?: boolean;
  favoriteNetworkPoint?: boolean;
  //   tsoShipperNote0: string;
  //   tsoShipperNote1: string;
};

export type SecondaryTrades = Collection<SecondaryTradeSummary>;

export type TradeProposals = Collection<SimpleTradeProposal>;
